<template>
  <div class="mt-print-preview">
    <div class="mt-print-preview__header">
      <div class="mt-print-preview__header-left">
        <strong>{{ soNo || $t('modern_trade.print_preview.title') }}</strong>
      </div>
      <div class="mt-print-preview__header-right">
        <el-button size="small" @click="closeWindow">
          {{ $t('modern_trade.print_preview.close_window') }}
        </el-button>
        <el-button size="small" type="primary" icon="el-icon-printer" @click="printDoc">
          {{ $t('modern_trade.print_preview.print') }}
        </el-button>
        <el-button
          size="small"
          icon="el-icon-download"
          circle
          :disabled="!blobUrl"
          :title="$t('modern_trade.print_preview.download')"
          @click="downloadDoc"
        ></el-button>
      </div>
    </div>

    <div class="mt-print-preview__body">
      <div
        class="mt-print-preview__main"
        v-loading="loading"
        :element-loading-text="$t('modern_trade.print_preview.loading')"
      >
        <iframe
          v-if="blobUrl"
          ref="frame"
          :src="blobUrl"
          class="mt-print-preview__frame"
          frameborder="0"
        ></iframe>
        <div v-else-if="errorMsg" class="mt-print-preview__empty text-danger">
          <i class="fa fa-exclamation-triangle mr-1"></i>{{ errorMsg }}
        </div>
        <div v-else-if="!loading" class="mt-print-preview__empty text-muted">
          {{ $t('modern_trade.print_preview.no_docs') }}
        </div>
      </div>

      <div class="mt-print-preview__sidebar">
        <h6 class="mt-print-preview__sidebar-title">{{ $t('modern_trade.print_preview.document') }}</h6>
        <div v-for="g in docGroups" :key="g.type" class="mt-print-preview__row">
          <div class="mt-print-preview__row-label">{{ g.label }}</div>
          <div class="mt-print-preview__row-controls">
            <el-checkbox v-model="g.original" @change="reload">
              {{ $t('modern_trade.print_preview.original') }}
            </el-checkbox>
            <el-checkbox v-model="g.copy" @change="reload">
              {{ $t('modern_trade.print_preview.copy') }}
            </el-checkbox>
            <el-input-number
              v-model="g.qty"
              :min="1"
              :max="10"
              size="mini"
              controls-position="right"
              class="mt-print-preview__qty"
              @change="reload"
            ></el-input-number>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Button, Checkbox, InputNumber, Message } from "element-ui";
import { ModernTradeService } from "@/resource";

// #183 — full-screen print-set preview page (SO/MO/DO/IV, original/copy + copies count).
// Route: /modern-trade/print-preview/:soId (top-level, NOT under the DashboardLayout
// /modern-trade children — this page must render fullscreen with no sidebar/menu).
const DOC_TYPE_ORDER = ["so", "invoice", "delivery", "production"];
const DOC_TYPE_LABEL_KEY = {
  so: "doc_so",
  invoice: "doc_invoice",
  delivery: "doc_delivery",
  production: "doc_production",
};
// หน้านี้ถูกเปิดจากปุ่ม PDF หลายตัว (ใบสั่งขาย / ใบสั่งผลิต / ใบส่งของ / ใบกำกับภาษี)
// ปุ่มต้นทางบอกว่าต้องการเอกสารไหนผ่าน query `doc` — ถ้าไม่มี/ไม่รู้จัก (bookmark, refresh,
// กด back) ถือว่าไม่ได้ระบุ แล้วกลับไปใช้ค่าเริ่มต้นเดิมคือ "ใบสั่งขาย".

export default {
  name: "MtPrintPreview",
  components: {
    [Button.name]: Button,
    [Checkbox.name]: Checkbox,
    [InputNumber.name]: InputNumber,
  },
  data() {
    return {
      soId: this.$route.params.soId,
      soNo: this.$route.query.so_no || "",
      // ประเภทเอกสารที่ปุ่มต้นทางขอมา — ใช้ตั้งค่าติ๊กเริ่มต้น "ครั้งเดียว" ตอน buildGroups เท่านั้น
      // หลังจากนั้นการติ๊ก/ปลดติ๊กเป็นของผู้ใช้ ไม่มีอะไรมาเขียนทับ (จงใจไม่ทำ watcher บน $route)
      requestedType: DOC_TYPE_ORDER.indexOf(this.$route.query.doc) >= 0 ? this.$route.query.doc : "",
      loading: false,
      errorMsg: "",
      docGroups: [],
      blobUrl: "",
    };
  },
  computed: {
    // Build the `docs` spec string: "type:copytype:qty" units, in the order
    // so, invoice, delivery, production — one unit per checked original/copy.
    docsSpec() {
      const parts = [];
      this.docGroups.forEach((g) => {
        if (g.original) parts.push(`${g.type}:original:${g.qty}`);
        if (g.copy) parts.push(`${g.type}:copy:${g.qty}`);
      });
      return parts.join(",");
    },
  },
  mounted() {
    this.loadOptions();
  },
  beforeDestroy() {
    this.revokeBlob();
  },
  methods: {
    async loadOptions() {
      this.loading = true;
      this.errorMsg = "";
      try {
        const res = await ModernTradeService.printOptions(this.soId);
        const list = (res.data && res.data.Data) || [];
        this.buildGroups(list);
        const soRow = list.find((d) => d.type === "so");
        if (soRow && soRow.doc_no) this.soNo = soRow.doc_no;
        await this.reload();
      } catch (err) {
        this.errorMsg = this.errMsg(err);
        this.loading = false;
      }
    },
    buildGroups(list) {
      const available = DOC_TYPE_ORDER.filter((type) => list.some((d) => d.type === type));
      const checkedType = this.defaultCheckedType(available);
      this.docGroups = available.map((type) => ({
        type,
        label: this.docLabel(type),
        docNos: list
          .filter((d) => d.type === type)
          .map((d) => d.doc_no)
          .filter(Boolean),
        // ติ๊กเอกสารที่ปุ่มต้นทางขอมา (เดิม hard-code เป็น "so" ทุกกรณี = บั๊กที่ user เจอ)
        original: type === checkedType,
        copy: false,
        qty: 1,
      }));
      // กดปุ่มใบส่งของ/ใบสั่งผลิต/ใบกำกับ แต่ใบสั่งขายใบนี้ยังไม่มีเอกสารนั้นจริง — เราติ๊กตัวสำรองให้
      // แล้วบอกตรง ๆ ว่าที่เห็นอยู่ไม่ใช่ตัวที่กดมา จะได้ไม่เผลอพิมพ์ผิดใบ (เตือนแบบไม่บล็อกหน้าจอ)
      if (this.requestedType && checkedType && checkedType !== this.requestedType) {
        Message({
          message: this.$t("modern_trade.print_preview.doc_not_available", {
            doc: this.docLabel(this.requestedType),
            fallback: this.docLabel(checkedType),
          }),
          type: "warning",
          duration: 6000,
          showClose: true,
        });
      }
    },
    // ลำดับการเลือกเอกสารเริ่มต้น:
    //   1) ตัวที่ปุ่มขอมา ถ้าใบสั่งขายนี้มีเอกสารนั้นอยู่จริง
    //   2) "ใบสั่งขาย" — BE คืน type=so เสมอ และเป็นพฤติกรรมเดิมก่อนแก้บั๊ก จึงปลอดภัยกับคนที่
    //      เปิดหน้านี้จาก bookmark/refresh/กด back (ไม่มี query `doc`)
    //   3) ตัวแรกที่มี — กันเคสสุดขอบที่ so หายไป ไม่ให้จบด้วยหน้าที่ไม่ติ๊กอะไรเลยแล้วขึ้น
    //      "ไม่มีเอกสาร" ทั้งที่ยังมีเอกสารอื่นให้พิมพ์
    defaultCheckedType(available) {
      if (this.requestedType && available.indexOf(this.requestedType) >= 0) return this.requestedType;
      if (available.indexOf("so") >= 0) return "so";
      return available[0] || "";
    },
    docLabel(type) {
      return this.$t(`modern_trade.print_preview.${DOC_TYPE_LABEL_KEY[type]}`);
    },
    async reload() {
      const spec = this.docsSpec;
      if (!spec) {
        this.revokeBlob();
        this.errorMsg = this.$t("modern_trade.print_preview.no_docs");
        return;
      }
      this.loading = true;
      this.errorMsg = "";
      try {
        const res = await ModernTradeService.printSet(this.soId, spec);
        const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: "application/pdf" });
        this.revokeBlob();
        this.blobUrl = window.URL.createObjectURL(blob);
      } catch (err) {
        this.errorMsg = this.errMsg(err);
      } finally {
        this.loading = false;
      }
    },
    revokeBlob() {
      if (this.blobUrl) {
        window.URL.revokeObjectURL(this.blobUrl);
        this.blobUrl = "";
      }
    },
    closeWindow() {
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        window.close();
      }
    },
    printDoc() {
      const frame = this.$refs.frame;
      if (frame && frame.contentWindow) {
        frame.contentWindow.focus();
        frame.contentWindow.print();
      }
    },
    downloadDoc() {
      if (!this.blobUrl) return;
      const a = document.createElement("a");
      a.href = this.blobUrl;
      a.download = `${this.soNo || "print"}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },
    errMsg(err) {
      return (err && err.response && err.response.data && err.response.data.Cause) || (err && err.message) || "Error";
    },
  },
};
</script>

<style scoped>
.mt-print-preview {
  display: flex;
  flex-direction: column;
  width: 100vw;
  height: 100vh;
  background: #525659;
}
.mt-print-preview__header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.25rem;
  background: #fff;
  border-bottom: 1px solid #e9ecef;
  color: #32325d;
}
.mt-print-preview__header-right > * {
  margin-left: 0.5rem;
}
.mt-print-preview__body {
  flex: 1 1 auto;
  display: flex;
  min-height: 0;
}
.mt-print-preview__main {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
}
.mt-print-preview__frame {
  width: 100%;
  height: 100%;
  display: block;
  border: none;
}
.mt-print-preview__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: 1rem;
  background: #fff;
}
.mt-print-preview__sidebar {
  flex: 0 0 280px;
  width: 280px;
  height: 100%;
  overflow-y: auto;
  background: #fff;
  border-left: 1px solid #e9ecef;
  padding: 1rem;
}
.mt-print-preview__sidebar-title {
  margin-bottom: 0.75rem;
  /* hard-code ในหน้านี้เอง ไม่ได้มาจากธีม ⇒ แก้ธีมกลางไม่ช่วย
     #8898aa บนพื้นขาว = 2.95:1 ที่ 12px uppercase — #627489 เฉดเดิม 4.80:1 */
  color: #627489;
  text-transform: uppercase;
  font-size: 0.75rem;
}
.mt-print-preview__row {
  padding: 0.6rem 0;
  border-bottom: 1px solid #f0f0f0;
}
.mt-print-preview__row-label {
  font-weight: 600;
  margin-bottom: 0.4rem;
}
.mt-print-preview__row-controls {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

/* หน้านี้เป็นหน้าเดียวใน MT ที่พื้นหลังเข้ม (.mt-print-preview = #525659)
   components/LoadingPanel.vue ปล่อย <style> ที่ "ไม่ scoped" ออกมาทั้งแอป:
     .el-loading-mask { background: transparent !important }
     .el-loading-spinner .path { stroke: #66615b !important }
   ⇒ ตอน v-loading ของ __main ทำงาน ไม่มีฉากขาวมาบัง สปินเนอร์กับข้อความจึง
   ตกลงบน #525659 ตรง ๆ — วัดได้ stroke 1.21:1 (แทบมองไม่เห็น) และ
   ข้อความ #409eff 2.66:1
   ธีมกลางที่จูนมาสำหรับพื้นอ่อนเอื้อมไม่ถึงเคสนี้ เพราะพื้นเข้มมาจากหน้านี้เอง
   #d5dbe3 บน #525659 = 5.32:1 (ตัวอักษรผ่าน AA, สปินเนอร์ผ่าน 1.4.11 ที่ 3:1)
   จำกัดขอบเขตด้วย .mt-print-preview จึงไม่กระทบ v-loading ของหน้าอื่น */
.mt-print-preview >>> .el-loading-spinner .el-loading-text {
  color: #d5dbe3;
}
.mt-print-preview >>> .el-loading-spinner .path {
  stroke: #d5dbe3 !important;
}
.mt-print-preview__row-controls .el-checkbox {
  margin-right: 0.75rem;
}
.mt-print-preview__qty {
  width: 100px;
}
</style>
